// BrandProvider — exposes the effective brand to the app shell and injects
// CSS variables for the owner's primary + accent colors.
//
// Design decisions (NOT in the plan; documented here):
//   1. Where the logo renders: AppSidebar header. The provider hands its
//      `logoUrl` to consumers via `useBrand()`; AppSidebar reads it and
//      shows the custom <img> when present, falling back to the default
//      <Logo /> SVG when null.
//   2. How colors are injected: the provider writes `--brand-primary` and
//      `--brand-accent` to `document.documentElement.style`. Tailwind /
//      component CSS opts into these via the existing `--primary` /
//      `--accent` tokens — we override them when the brand is customized.
//      When `isCustomized === false` the provider removes the variables so
//      the platform defaults from index.css take over.
//   3. Non-Studio is a strict no-op: `useEffectiveBrand` already returns
//      defaults when isStudio is false, so the effect below clears any
//      previously-set variables and the DOM looks identical to a fresh
//      load.

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useEffectiveBrand, type EffectiveBrand } from '@/hooks/useEffectiveBrand';

const BrandContext = createContext<EffectiveBrand | null>(null);

const PRIMARY_VAR = '--brand-primary';
const ACCENT_VAR = '--brand-accent';

interface BrandProviderProps {
  children: ReactNode;
  /** Optional document target — defaults to `document.documentElement`. Tests pass a mock. */
  target?: HTMLElement;
}

export function BrandProvider({ children, target }: BrandProviderProps) {
  const brand = useEffectiveBrand();

  useEffect(() => {
    const root = target ?? (typeof document !== 'undefined' ? document.documentElement : null);
    if (!root) return;

    if (brand.isCustomized && brand.primaryColor) {
      root.style.setProperty(PRIMARY_VAR, brand.primaryColor);
    } else {
      root.style.removeProperty(PRIMARY_VAR);
    }

    if (brand.isCustomized && brand.accentColor) {
      root.style.setProperty(ACCENT_VAR, brand.accentColor);
    } else {
      root.style.removeProperty(ACCENT_VAR);
    }
  }, [brand.isCustomized, brand.primaryColor, brand.accentColor, target]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

/**
 * useBrand — returns the active brand. Outside a BrandProvider it returns a
 * no-op default (i.e. "no customization"), so direct unit tests of
 * components that read the brand don't need to mount the provider.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBrand(): EffectiveBrand {
  const value = useContext(BrandContext);
  if (value) return value;
  return {
    logoUrl: null,
    primaryColor: null,
    accentColor: null,
    isCustomized: false,
    isLoading: false,
  };
}
